import { useMemo, type ReactNode } from 'react'
import { Link } from 'react-router-dom'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { useTareas } from '@/hooks/useTareas'
import { usePermissions } from '@/hooks/usePermissions'
import { agruparTareasPanel } from './panel-utils'
import { formatDate } from '@/lib/utils'
import type { TareaCompleta } from '@/types/database'

const MAX_VISIBLES = 5

const SKELETON = (
  <div className="space-y-3">
    {[1, 2, 3, 4].map((i) => (
      <div key={i} className="space-y-1">
        <div className="h-4 w-48 bg-muted animate-pulse rounded" />
        <div className="h-3 w-32 bg-muted animate-pulse rounded" />
      </div>
    ))}
  </div>
)

function PanelCard({ children }: { children: ReactNode }) {
  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between space-y-0">
        <CardTitle>Tareas pendientes</CardTitle>
        <Link to="/tareas" className="text-sm text-muted-foreground hover:text-foreground">
          Ver todas
        </Link>
      </CardHeader>
      <CardContent>{children}</CardContent>
    </Card>
  )
}

function FilaTarea({ tarea, vencida }: { tarea: TareaCompleta; vencida?: boolean }) {
  return (
    <div className="flex items-center justify-between gap-2">
      <div className="min-w-0">
        <p className="text-sm font-medium truncate">{tarea.titulo}</p>
        {tarea.cliente_nombre && (
          <p className="text-xs text-muted-foreground truncate">{tarea.cliente_nombre}</p>
        )}
      </div>
      <span
        className={
          vencida ? 'text-xs font-medium text-red-600 shrink-0' : 'text-xs text-muted-foreground shrink-0'
        }
      >
        {tarea.fecha_limite ? formatDate(tarea.fecha_limite) : ''}
      </span>
    </div>
  )
}

function GrupoTareas({
  titulo,
  tareas,
  vencidas,
  urgente,
}: {
  titulo: string
  tareas: TareaCompleta[]
  vencidas?: boolean
  urgente?: boolean
}) {
  if (tareas.length === 0) return null
  const tituloColor = vencidas ? 'text-red-600' : urgente ? 'text-amber-600' : 'text-muted-foreground'
  const accentColor = vencidas ? 'border-red-400' : urgente ? 'border-amber-400' : 'border-slate-300'
  return (
    <div className={`border-l-2 ${accentColor} pl-3`}>
      <p className={`text-xs font-semibold uppercase tracking-wide mb-2 ${tituloColor}`}>
        {titulo} ({tareas.length})
      </p>
      <div className="space-y-2">
        {tareas.slice(0, MAX_VISIBLES).map((tarea) => (
          <FilaTarea key={tarea.id ?? tarea.titulo ?? ''} tarea={tarea} vencida={vencidas} />
        ))}
        {tareas.length > MAX_VISIBLES && (
          <p className="text-xs text-muted-foreground">+{tareas.length - MAX_VISIBLES} más</p>
        )}
      </div>
    </div>
  )
}

export function TareasPendientesCard() {
  const { esAsistente, emailUsuario } = usePermissions()

  // Asistente without a resolved email yet: don't mount the query (avoids an
  // empty-filter fetch plus a refetch when the email arrives). Fail-closed.
  if (esAsistente && !emailUsuario) {
    return <PanelCard>{SKELETON}</PanelCard>
  }

  return <TareasPendientesContent esAsistente={esAsistente} emailUsuario={emailUsuario} />
}

function TareasPendientesContent({
  esAsistente,
  emailUsuario,
}: {
  esAsistente: boolean
  emailUsuario: string | null
}) {
  // Same scoping as the Tareas page: asistente only sees their own tasks
  const { data: tareas = [], isLoading } = useTareas(
    esAsistente ? { asignado_a: emailUsuario ?? '' } : undefined
  )

  const { urgentes, vencidas, deHoy, estaSemana } = useMemo(() => agruparTareasPanel(tareas), [tareas])
  const vacio =
    urgentes.length === 0 && vencidas.length === 0 && deHoy.length === 0 && estaSemana.length === 0

  return (
    <PanelCard>
      {isLoading ? (
        SKELETON
      ) : vacio ? (
        <p className="text-sm text-muted-foreground text-center py-4">
          No hay tareas pendientes
        </p>
      ) : (
        <div className="space-y-4">
          <GrupoTareas titulo="Urgentes" tareas={urgentes} urgente />
          <GrupoTareas titulo="Vencidas" tareas={vencidas} vencidas />
          <GrupoTareas titulo="Para hoy" tareas={deHoy} />
          <GrupoTareas titulo="Esta semana" tareas={estaSemana} />
        </div>
      )}
    </PanelCard>
  )
}
